import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import Tooltip from "../common/Tooltip";
import ClubPicker from "./ClubPicker";
import CommissionSplitField from "./CommissionSplitField";
import AgentTenderCard from "./AgentTenderCard";
import { InfoGlyph, LoupeGlyph } from "./glyphs";
import { agentTenderService } from "../../services/agentTenderApi";
import {
  AGENT_SUBJECT_CHOICES,
  AGENT_LEVEL_CHOICES,
  AGENT_STAFF_ROLE_CHOICES,
  CURRENCY_CHOICES,
  MARKET_VALUE_CHOICES,
  FOOT_CHOICES,
  GAMES_PLAYED_CHOICES,
  STAFF_START_TYPE_CHOICES,
  DURATION_UNIT_CHOICES,
  formatTargetWindow,
  genericWindowChoices,
  getPositionsForCategory,
  getTypeChoicesForCategory,
  normalizeAgentLevel,
} from "../../constants/agentTenderChoices";

/* ── Verbatim artboard SVGs (V2 UI rebuild, RULE 2) — every path copied 1:1
   from design doc 02/03. Never a platform icon that merely resembles them. ── */

const IcEye = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IcEyeOff = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M17.9 17.9A10.9 10.9 0 0 1 12 19c-7 0-11-7-11-7a20.3 20.3 0 0 1 5.1-5.9M9.9 4.2A10.9 10.9 0 0 1 12 5c7 0 11 7 11 7a20.4 20.4 0 0 1-2.4 3.4" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </svg>
);

// Info circle + loupe come from the SHARED glyph set (glyphs.jsx, FEED's
// merge) — same verbatim paths, single source.
const IcInfo = () => <InfoGlyph size={12} className="text-[#94A3B8]" />;

const IcChevronDown = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.4" strokeLinecap="round" {...props}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const IcLock = (props) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" {...props}>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const IcPencil = (props) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
  </svg>
);

export const IcWarnTriangle = ({ size = 15, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

/* ── Form primitives per artboard (doc 02) ─────────────────────────────────── */

// White field-section card: 12px radius, #E2E8F0 ring, 18/20 padding, 13.5px/800
// heading ("Client club", "Request details", "Requirements", "Commission").
const Section = ({ title, required, children }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-4 sm:px-5 sm:py-[18px] flex flex-col gap-3.5">
    {/* A card whose heading IS the field label (Client club) carries the same
        asterisk a Field label would — the picker inside keeps `label=""` so the
        heading stays single. */}
    <h2 className="text-[13.5px] font-extrabold text-[#0F172A]">
      {title}
      {required && <span className="text-[#DC2626] ml-1">*</span>}
    </h2>
    {children}
  </div>
);

// Label row (12.5px/600 #334155) + optional grey sub, verbatim info-circle in a
// tooltip, control, then hint BELOW the control (11.5px, grey or amber).
const Field = ({ label, sub, required, tooltip, children, hint, hintTone, error, className = "" }) => (
  <div className={className}>
    <div className="flex items-center gap-[5px] mb-1.5">
      <span className="text-[12.5px] font-semibold text-[#334155]">
        {label}
        {required && <span className="text-[#DC2626] ml-1">*</span>}
        {sub && <span className="font-medium text-[#94A3B8]"> {sub}</span>}
      </span>
      {tooltip && (
        <Tooltip content={tooltip} position="top" className="whitespace-normal max-w-[240px]">
          <span className="inline-flex cursor-default"><IcInfo /></span>
        </Tooltip>
      )}
    </div>
    {children}
    {hint && (
      <p className={`mt-[5px] text-[11.5px] ${hintTone === "amber" ? "text-[#B45309] font-semibold" : "text-[#94A3B8]"}`}>
        {hint}
      </p>
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

// 40px input box with optional inline prefix/suffix (grey, inside the border) —
// the artboard's "90,000 from / month" field grammar. The native input keeps
// type/placeholder/value untouched (payload building is unchanged).
const inputReset =
  "min-w-0 flex-1 bg-transparent outline-none text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] [font-variant-numeric:tabular-nums] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

const InputBox = ({ prefix, suffix, disabled, className = "", inputProps }) => (
  <div
    className={`h-10 flex items-center gap-1.5 px-3 rounded-[9px] border text-[13.5px] ${
      disabled
        ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed"
        : "bg-white border-[#E2E8F0] focus-within:border-[#2283FF] focus-within:shadow-[0_0_0_3px_#E8F1FF]"
    } ${className}`}
  >
    {prefix && <span className="flex-none text-[#94A3B8]">{prefix}</span>}
    <input disabled={disabled} className={inputReset} {...inputProps} />
    {suffix && <span className="flex-none text-[#94A3B8]">{suffix}</span>}
  </div>
);

const textareaCls =
  "w-full border border-[#E2E8F0] rounded-[9px] bg-white px-3 py-2.5 text-[13px] text-[#334155] outline-none focus:border-[#2283FF] focus:shadow-[0_0_0_3px_#E8F1FF] placeholder:text-[#94A3B8]";

// Segmented Subject/Level toggle exactly as drawn: #F1F5F9 track, 3px inset,
// active segment = white, 8px radius, bold, 1px/2px shadow.
const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div className="inline-flex w-full rounded-[10px] bg-[#F1F5F9] p-[3px]" role="radiogroup" aria-label={ariaLabel}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        aria-pressed={value === o.value}
        className={`flex-1 text-center text-[13px] py-2 rounded-lg ${
          value === o.value
            ? "bg-white font-bold text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,.08)]"
            : "font-medium text-[#64748B]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Mini segmented (Net/Gross, Range/Fixed): 9px-radius track, 2px inset.
const MiniSegmented = ({ options, value, onChange, ariaLabel }) => (
  <span className="inline-flex rounded-[9px] bg-[#F1F5F9] p-[2px]" role="radiogroup" aria-label={ariaLabel}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        aria-pressed={value === o.value}
        className={`text-xs px-3 py-[5px] ${
          value === o.value
            ? "bg-white rounded-[7px] font-bold text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,.08)]"
            : "font-medium text-[#64748B]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </span>
);

// B2 (Commissioned Search) lock glyph + wrapper. When the form runs as a
// commission hand-over the CORE search criteria come from the club's brief and
// are LOCKED, server-enforced (Marin / Annex B2): the agent cannot change them.
// `Locked` disables the wrapped control (pointer-events + opacity) and marks it
// read-only; the prefilled value is what posts, and the backend overrides
// anything a client smuggles. Off by default → zero effect on ordinary
// create/edit.
const IcShieldCheck = (props) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 2l7 3v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V5z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const Locked = ({ on, children }) =>
  on ? (
    <div aria-disabled="true">
      <div className="pointer-events-none opacity-70 select-none">{children}</div>
      <p className="mt-[5px] inline-flex items-center gap-1 text-[11px] font-semibold text-[#64748B]">
        <IcLock /> Set by the club
      </p>
    </div>
  ) : (
    children
  );

// Selection pill (request type, target window, foot, staff start date).
const Pill = ({ active, onClick, children, title, large }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`rounded-full ${large ? "text-[13px] px-[18px] py-2" : "text-xs px-3 py-[7px]"} ${
      active
        ? "bg-[#2283FF] border border-[#2283FF] text-white font-bold"
        : "bg-white border border-[#E2E8F0] text-[#475569] font-medium hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

// Checkbox as drawn: 16px, 4px radius, checked = solid #2283FF + verbatim
// check path (3.4 stroke). Native input stays (sr-only) so labels/roles and
// all existing handlers are untouched.
const Checkbox = ({ checked, onChange, disabled, children }) => (
  <label
    className={`inline-flex items-center gap-[7px] text-[13px] ${
      disabled ? "text-gray-400 cursor-not-allowed" : "text-[#334155] cursor-pointer"
    }`}
  >
    <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} disabled={disabled} />
    <span className="w-4 h-4 flex-none rounded border-[1.5px] border-[#CBD5E1] bg-white inline-flex items-center justify-center text-transparent peer-checked:bg-[#2283FF] peer-checked:border-[#2283FF] peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-[#2283FF]/40">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12l5 5L20 7" />
      </svg>
    </span>
    {children}
  </label>
);

// Radio as drawn: 15px circle, selected = 4.5px #2283FF ring.
const Radio = ({ name, checked, onChange, children }) => (
  <label className="inline-flex items-center gap-1.5 text-[12.5px] cursor-pointer">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      className="appearance-none w-[15px] h-[15px] flex-none rounded-full border-[1.5px] border-[#CBD5E1] bg-white checked:border-[4.5px] checked:border-[#2283FF] focus-visible:ring-2 focus-visible:ring-[#2283FF]/40"
    />
    <span className={checked ? "text-[#334155]" : "text-[#64748B]"}>{children}</span>
  </label>
);

// Select per artboard: 40px trigger with the verbatim chevron; open menu =
// 10px radius, 0 12px 32px shadow, 9/13 rows, selected row #E8F1FF. Searchable
// variant turns the trigger into the artboard's focused search field (loupe +
// blue ring). Purely presentational — same value in, same value out.
const FormSelect = ({ options, value, onChange, placeholder = "Select an option", searchable = false, error = false, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        setQ("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered =
    searchable && q
      ? options.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()))
      : options;

  return (
    <div className={`relative ${className}`} ref={boxRef}>
      {open && searchable ? (
        <div className="h-10 flex items-center gap-2 px-3 rounded-[9px] bg-white border-[1.5px] border-[#2283FF] shadow-[0_0_0_3px_#E8F1FF]">
          <span className="flex-none inline-flex text-[#94A3B8]"><LoupeGlyph /></span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={placeholder}
            className="w-full min-w-0 bg-transparent outline-none text-[13.5px] placeholder:text-[#94A3B8]"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`h-10 w-full flex items-center justify-between gap-2 px-3 rounded-[9px] bg-white border text-left ${
            error ? "border-red-500" : "border-[#E2E8F0]"
          }`}
        >
          <span className={`truncate text-[13.5px] ${selected ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
            {selected ? selected.label : placeholder}
          </span>
          <IcChevronDown className="flex-none" />
        </button>
      )}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-20 bg-white border border-[#E2E8F0] rounded-[10px] shadow-[0_12px_32px_rgba(15,23,42,.14)] overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-[13px] py-[9px] text-[13px] text-[#94A3B8]">No options found</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQ("");
                  }}
                  className={`w-full text-left px-[13px] py-[9px] text-[13px] flex items-center gap-1.5 ${
                    o.value === value
                      ? "bg-[#E8F1FF] font-semibold text-[#0F172A]"
                      : "text-[#334155] hover:bg-[#F8FAFC]"
                  } ${o.dividerBefore ? "border-t border-dashed border-[#EEF2F7]" : ""}`}
                >
                  {o.render || o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Local display maps (labels per artboard; values/payload untouched) ────── */

const CURRENCY_DISPLAY = { EUR: "€ EUR", GBP: "£ GBP", USD: "$ USD", CHF: "CHF" };
const CURRENCY_SYMBOL = { EUR: "€", GBP: "£", USD: "$" };
const CURRENCY_ORDER = { EUR: 0, GBP: 1, CHF: 2, USD: 3 };
const currencyOptions = [...CURRENCY_CHOICES]
  .sort((a, b) => (CURRENCY_ORDER[a.value] ?? 9) - (CURRENCY_ORDER[b.value] ?? 9))
  .map((c) => ({ value: c.value, label: CURRENCY_DISPLAY[c.value] || c.value }));

// Staff request type: full_time reads "Permanent" on the artboard (value + the
// catalog stay untouched — display only).
const TYPE_LABEL_OVERRIDES = { full_time: "Permanent" };

// Range/Fixed segment labels for the salary + market-value controls.
const SALARY_KIND_OPTIONS = [
  { value: "salary_between", label: "Range" },
  { value: "fixed_amount", label: "Fixed" },
];
const SALARY_CALC_OPTIONS = [
  { value: "net", label: "Net" },
  { value: "gross", label: "Gross" },
];
const MV_LABELS = {
  market_value_range: "Range",
  market_value_fixed: "Fixed",
  not_important: "Not important",
};
const GAMES_LABELS = { last_half_season: "Last half-season" };
const START_LABELS = { from_month: "From month…" };

// Foot pills in artboard order: Any · Left · Right.
const FOOT_ORDER = { A: 0, L: 1, R: 2 };
const footOptions = [...FOOT_CHOICES].sort(
  (a, b) => (FOOT_ORDER[a.value] ?? 9) - (FOOT_ORDER[b.value] ?? 9),
);

// Publication-Clarity-Box V2 (approved 2026-07-21), compact in-form variant as
// drawn on every one of the 7 form boards: Published (#E8F1FF) / Kept private
// (#F8FAFC) with the verbatim eye / eye-off marks. The new truth: real figures
// are published as entered; description + key requirements are PUBLIC.
const PublicationClarity = () => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-3.5 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
    <div className="bg-[#E8F1FF] rounded-[10px] px-[13px] py-[11px]">
      <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[.04em] uppercase text-[#1B6FE0]">
        <IcEye className="flex-none" />
        Published
      </div>
      <div className="text-[11.5px] text-[#1E3A5F] mt-[5px] leading-[1.55]">
        Role, tier, country, <b>real figures</b>, period, commission, spec chips,
        description &amp; key requirements, your agency + name.
      </div>
    </div>
    <div className="bg-[#F8FAFC] border border-[#EEF2F7] rounded-[10px] px-[13px] py-[11px]">
      <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-[.04em] uppercase text-[#475569]">
        <IcEyeOff className="flex-none" />
        Kept private
      </div>
      <div className="text-[11.5px] text-[#334155] mt-[5px] leading-[1.55]">
        <b>The club's identity</b>, your club contact, tolerance.{" "}
        <span className="text-[#B45309] font-semibold">Never name the club in free text.</span>
      </div>
    </div>
  </div>
);

const EMPTY = {
  club: null,
  tender_category: "player",
  position: "",
  // Staff requests key on a structured role from the platform catalog instead
  // of a free-text position (Rule 6). NULL for player requests.
  staff_role: "",
  type_of_tender: "buy",
  recruiting_for: "professional",
  // EUR is the default currency for agent requests (Marin override of D-005's
  // 1:1 mirror of the club list, which defaulted to USD). Option list unchanged.
  currency: "EUR",
  salary_calculation: "net",
  salary_type: "salary_between",
  salary_from: "",
  salary_to: "",
  salary_fixed: "",
  market_value_type: "not_important",
  market_value_from: "",
  market_value_to: "",
  market_value_fixed: "",
  min_age: "",
  max_age: "",
  minimum_height: "",
  // Age / height "Not important" toggles (Marin), same pattern as games-played:
  // "not_important" hides + clears the numeric fields and the payload omits them.
  age_range_in: "not_important",
  height_in: "not_important",
  // No pre-selected foot pill (Marin, DECISION-008): an untouched form must not
  // narrow the request to right-footers. "" = not specified = both feet, and the
  // payload omits the key entirely (same "omit untouched optionals" contract as
  // market value / games played, FE PR #156 / #227).
  foot: "",
  experience: "",
  // New requests default to NOT free-agents-only so the transfer-fee field is
  // usable by default; checking it clears + disables the fee (see below).
  only_free_players: false,
  only_eu_player: false,
  maximum_transfer_fee: "",
  loan_with_option_to_buy: true,
  has_loan_fee: false,
  loan_fee_amount: "",
  trial_start: "",
  trial_end: "",
  // Games-played requirement mirrors the club form's "Games played in" radio
  // (GAMES_PLAYED_CHOICES): last_season / last_half_season / not_important.
  games_played_in: "not_important",
  matches_played_last_season: "",
  matches_played_last_half_season: "",
  tolerance_value: "",
  description: "",
  key_requirements: "",
  commission_posting: "",
  commission_responding: "",
  commission_note: "",
  // ── V2 fields (approved 2026-07-21) ──
  // Player period selector: stable half-year key or "" = Not specified (null).
  target_window: "",
  // Staff start date: immediately / from_month (+ YYYY-MM) / not_specified.
  staff_start_type: "not_specified",
  staff_start_month: "",
  // Staff Temporary type: optional duration + unit.
  temporary_duration: "",
  temporary_duration_unit: "months",
  // Staff experience: free minimum years OR "Not important" (no fixed buckets).
  experience_min_years: "",
  experience_not_important: false,
  // Commission three-state: explicit "no commission possible for this job".
  no_commission: false,
  // Required free-text role title when staff_role === "other".
  other_staff_position: "",
};

// Two-option "Specify vs Not important" toggle for age range + minimum height
// (Marin) — the age/height analog of GAMES_PLAYED_CHOICES.
const IMPORTANCE_CHOICES = [
  { value: "specify", label: "Specify" },
  { value: "not_important", label: "Not important" },
];

// Shared create/edit form. `onSubmit(payload)` returns {success,error}.
// `initial` seeds the form for edit; `isVerified` shows the freeze warning.
const AgentTenderForm = ({
  initial,
  isVerified = false,
  submitLabel = "Post request",
  onSubmit,
  onCancel,
  // B2 hand-over: { locked: [form-field names], clubName }. When present the
  // form is a commissioned post — the core criteria are locked + Club Verified
  // from birth (no OTP). Null (the default) = ordinary agent request.
  commission = null,
}) => {
  const lockedSet = useMemo(() => new Set(commission?.locked || []), [commission]);
  const isLocked = (field) => lockedSet.has(field);
  const [form, setForm] = useState(() => {
    const merged = { ...EMPTY, ...(initial || {}) };
    // Derive the games-played radio from any preloaded match counts.
    if (initial && !initial.games_played_in) {
      if (Number(merged.matches_played_last_season) > 0)
        merged.games_played_in = "last_season";
      else if (Number(merged.matches_played_last_half_season) > 0)
        merged.games_played_in = "last_half_season";
      else merged.games_played_in = "not_important";
    }
    // Derive the age/height toggles from any preloaded values (edit mode).
    if (initial && !initial.age_range_in) {
      merged.age_range_in =
        Number(merged.min_age) > 0 || Number(merged.max_age) > 0
          ? "specify"
          : "not_important";
    }
    if (initial && !initial.height_in) {
      merged.height_in =
        Number(merged.minimum_height) > 0 ? "specify" : "not_important";
    }
    // Level (recruiting_for) is now required for BOTH subjects (Part B). Prefill
    // it for edit: a legacy staff row stored a blank recruiting_for, so normalize
    // to a valid level (Professional default) rather than leaving the Level
    // dropdown empty on a request that pre-dates the two-axis model.
    merged.recruiting_for = normalizeAgentLevel(merged.recruiting_for);
    return merged;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Club-name guard (V2): the backend soft-400s {code:'club_name_in_text'}
  // when the public free text appears to name the client club. We hold the
  // payload and warnings for the confirm dialog; "Publish anyway" re-sends the
  // SAME payload with confirm_club_name=true.
  const [clubNameGuard, setClubNameGuard] = useState(null);
  // Period selector choices. Client-side generic half-years are the fallback;
  // the window-choices endpoint (fetched per selected club) returns the same
  // stable keys, enriched with the club country's real open/close dates.
  const [windowChoices, setWindowChoices] = useState(() => genericWindowChoices());

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const positionChoices = useMemo(
    () => getPositionsForCategory(form.tender_category),
    [form.tender_category],
  );
  const typeChoices = useMemo(
    () => getTypeChoicesForCategory(form.tender_category),
    [form.tender_category],
  );

  // Staff-role options with the artboard's "Other…" row (pencil + italic hint,
  // dashed divider) — the catalog values themselves are untouched.
  const staffRoleOptions = useMemo(
    () =>
      AGENT_STAFF_ROLE_CHOICES.map((o) =>
        o.value === "other"
          ? {
              ...o,
              dividerBefore: true,
              render: (
                <>
                  <IcPencil className="flex-none" />
                  <span>Other…</span>
                  <span className="text-[#94A3B8] italic">write your own title</span>
                </>
              ),
            }
          : o,
      ),
    [],
  );

  const isStaff = form.tender_category === "staff";
  const isLoan = form.type_of_tender === "loan";
  const isTrial = form.type_of_tender === "trial";
  const isBuy = form.type_of_tender === "buy";

  // Fetch the club-country window choices once a club is picked (player path
  // only). On any failure the generic fallback stands — the selector always
  // renders, and the keys are identical either way.
  const clubRefKey = form.club?.platform_data?.exists
    ? `c:${form.club.platform_data.club_id}`
    : form.club?.wyscout_id
    ? `w:${form.club.wyscout_id}`
    : "";
  useEffect(() => {
    if (!clubRefKey || isStaff) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [kind, id] = clubRefKey.split(":");
        const res = await agentTenderService.getWindowChoices(
          kind === "c" ? { club_id: id } : { wyscout_club_id: id },
        );
        if (!cancelled && res?.success && res.data?.choices?.length) {
          setWindowChoices(res.data.choices);
        }
      } catch (e) {
        /* keep the generic fallback choices */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubRefKey, isStaff]);

  // Selector options: the rolling choices, plus the stored target_window of an
  // edited row when it already rotated out of the rolling set (so the active
  // selection always renders a pill).
  const windowOptions = useMemo(() => {
    if (
      form.target_window &&
      !windowChoices.some((c) => c.value === form.target_window)
    ) {
      return [
        {
          value: form.target_window,
          label: formatTargetWindow(form.target_window) || form.target_window,
          opens_at: null,
          closes_at: null,
        },
        ...windowChoices,
      ];
    }
    return windowChoices;
  }, [windowChoices, form.target_window]);

  const validate = () => {
    const e = {};
    if (!form.club) e.club = "Please select a club for this request.";
    if (isStaff) {
      // Staff request: a structured role from the catalog is required (the
      // backend rejects a staff request without staff_role with a 400).
      if (!form.staff_role) e.staff_role = "Please select a staff role.";
      // V2: "Other" reveals a required free-text role title.
      if (form.staff_role === "other" && !form.other_staff_position.trim())
        e.other_staff_position = "Please enter the role title.";
      if (
        form.staff_start_type === "from_month" &&
        !/^\d{4}-(0[1-9]|1[0-2])$/.test(form.staff_start_month)
      )
        e.staff_start_month = "Please pick the start month.";
      if (!form.experience_not_important && form.experience_min_years !== "") {
        const y = Number(form.experience_min_years);
        if (!Number.isFinite(y) || y < 0 || y > 60)
          e.experience_min_years = "Experience minimum must be between 0 and 60 years.";
      }
    } else if (!form.position) {
      e.position = "Please select a position.";
    }

    if (form.salary_type === "salary_between") {
      if (form.salary_from === "" || form.salary_to === "") {
        e.salary = "Please enter both a minimum and maximum salary for a range.";
      } else if (Number(form.salary_from) >= Number(form.salary_to)) {
        e.salary = "Minimum salary must be less than the maximum.";
      }
    } else if (form.salary_type === "fixed_amount") {
      if (form.salary_fixed === "") e.salary = "Please enter a fixed salary amount.";
      else if (!Number.isFinite(Number(form.salary_fixed)))
        e.salary = "Please enter a valid number.";
    }

    if (form.only_free_players && isBuy && Number(form.maximum_transfer_fee) > 0) {
      e.maximum_transfer_fee =
        'Free agents only cannot have a transfer fee. Remove the fee or uncheck "Only free players".';
    }

    if (isTrial) {
      if (!form.trial_start || !form.trial_end)
        e.trial = "Please set both a start and end date for the trial.";
      else if (new Date(form.trial_end) < new Date(form.trial_start))
        e.trial = "The trial end date must be on or after the start date.";
    }

    if (form.tolerance_value !== "") {
      const t = Number(form.tolerance_value);
      if (!Number.isFinite(t) || t < 0 || t > 50)
        e.tolerance_value = "Tolerance must be between 0 and 50.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Numeric optionals the backend declares as integers: an empty string means
  // "not specified" in the form, but DRF integer fields reject "" with
  // "A valid integer is required." (the create then 400s and the form's own
  // "not_important hides + clears" contract is broken). Omitting the key is
  // the documented behaviour, so strip empty values before sending.
  const NUMERIC_OPTIONALS = [
    "min_age",
    "max_age",
    "minimum_height",
    "matches_played_last_season",
    "matches_played_last_half_season",
    "maximum_transfer_fee",
    "loan_fee_amount",
    "market_value_from",
    "market_value_to",
    "fix_market_value",
    "net_salary_from",
    "net_salary_to",
    "gross_salary_from",
    "gross_salary_to",
    "fix_net_salary",
    "fix_gross_salary",
  ];

  const pruneEmptyNumerics = (payload) => {
    const out = { ...payload };
    NUMERIC_OPTIONALS.forEach((key) => {
      if (out[key] === "" || out[key] === null || out[key] === undefined) {
        delete out[key];
      }
    });
    return out;
  };

  const buildPayload = () => pruneEmptyNumerics({
    // Exactly one of club_id / wyscout_club_id (DoD 23). The general club search
    // returns Wyscout-based hits; platform_data.exists says whether the club is
    // already on the platform (→ club_id) or a catalog club (→ wyscout_club_id,
    // backend creates an ownerless club).
    ...(form.club?.platform_data?.exists
      ? { club_id: form.club.platform_data.club_id }
      : { wyscout_club_id: form.club?.wyscout_id }),
    tender_category: form.tender_category,
    // Staff keys on the structured staff_role; player keeps position. Never
    // both — the backend dedup fingerprint uses one or the other by category.
    position: isStaff ? "" : form.position || "",
    staff_role: isStaff ? form.staff_role : "",
    // V2: "Other" staff role carries its required free-text title; any other
    // role clears it so a role switch never leaves a stale title behind.
    other_staff_position:
      isStaff && form.staff_role === "other"
        ? form.other_staff_position.trim()
        : "",
    type_of_tender: form.type_of_tender,
    // Level axis (Part B): recruiting_for is required for BOTH subjects and is
    // sent for staff too — never cleared. Normalized so it is always a valid
    // professional/youth value.
    recruiting_for: normalizeAgentLevel(form.recruiting_for),
    currency: form.currency,
    salary_calculation_type: form.salary_calculation,
    salary_type: form.salary_type,
    ...(form.salary_type === "salary_between"
      ? form.salary_calculation === "gross"
        ? { gross_salary_from: form.salary_from, gross_salary_to: form.salary_to }
        : { net_salary_from: form.salary_from, net_salary_to: form.salary_to }
      : form.salary_calculation === "gross"
      ? { fix_gross_salary: form.salary_fixed }
      : { fix_net_salary: form.salary_fixed }),
    // V2 staff-path cleanup: market value is a player-only concept — the
    // backend rejects substantive values for staff, so staff omits the whole
    // block (a stale player selection never leaks into a staff payload).
    ...(!isStaff
      ? {
          market_value_type: form.market_value_type,
          ...(form.market_value_type === "market_value_range"
            ? {
                market_value_from: form.market_value_from,
                market_value_to: form.market_value_to,
              }
            : form.market_value_type === "market_value_fixed"
            ? { fix_market_value: form.market_value_fixed }
            : {}),
        }
      : {}),
    min_age: form.age_range_in === "specify" ? form.min_age : "",
    max_age: form.age_range_in === "specify" ? form.max_age : "",
    // Player-only spec fields — same V2 staff-path cleanup as market value and
    // games played above. Minimum height, preferred foot and the free/EU
    // eligibility checkboxes all live behind `!isStaff` in the form, so a staff
    // request must never carry them. `foot` used to default to "R", which is why
    // the live preview rail (the shared AgentTenderCard fed with buildPayload())
    // painted a "Right foot" chip on every staff request — getSpecChips reads
    // these straight off the payload. Precedent: the sibling club-network
    // create form, CreateSearchRequest.js (`foot: isStaff ? null : ...`).
    minimum_height:
      !isStaff && form.height_in === "specify" ? form.minimum_height : "",
    // DECISION-008: an unchosen foot is OMITTED, never sent as an empty value.
    ...(!isStaff && form.foot ? { foot: form.foot } : {}),
    // Legacy experience bucket: no V2 UI, but an edited staff row must re-emit
    // its stored value untouched (never wipe data the form no longer shows).
    experience: form.experience,
    only_free_players: !isStaff && form.only_free_players,
    only_eu_player: !isStaff && form.only_eu_player,
    ...(isBuy
      ? {
          maximum_transfer_fee: form.only_free_players
            ? ""
            : form.maximum_transfer_fee,
        }
      : {}),
    ...(isLoan
      ? {
          loan_with_option_to_buy: form.loan_with_option_to_buy,
          has_loan_fee: form.has_loan_fee,
          loan_fee_amount: form.has_loan_fee ? form.loan_fee_amount : "",
        }
      : {}),
    ...(isTrial
      ? {
          trial_period_start_date: form.trial_start,
          trial_period_end_date: form.trial_end,
        }
      : {}),
    // Games played: player-only (V2 staff cleanup — the backend rejects staff
    // values, so staff omits the keys entirely).
    ...(!isStaff
      ? form.games_played_in === "last_season"
        ? {
            matches_played_last_season: form.matches_played_last_season,
            matches_played_last_half_season: "",
          }
        : form.games_played_in === "last_half_season"
        ? {
            matches_played_last_season: "",
            matches_played_last_half_season: form.matches_played_last_half_season,
          }
        : { matches_played_last_season: "", matches_played_last_half_season: "" }
      : {}),
    // V2 period fields — player: the chosen target window ('' = Not specified
    // = null); staff: start date + Temporary duration + experience minimum.
    ...(!isStaff
      ? { target_window: form.target_window || null }
      : {
          staff_start_type: form.staff_start_type || "not_specified",
          ...(form.staff_start_type === "from_month"
            ? { staff_start_month: form.staff_start_month }
            : {}),
          ...(form.type_of_tender === "temporary"
            ? {
                temporary_duration:
                  form.temporary_duration === ""
                    ? null
                    : Number(form.temporary_duration),
                temporary_duration_unit: form.temporary_duration_unit,
              }
            : {}),
          experience_not_important: !!form.experience_not_important,
          experience_min_years:
            form.experience_not_important || form.experience_min_years === ""
              ? null
              : Number(form.experience_min_years),
        }),
    // Tolerance stays optional on the wire, but since the 2026-08-12
    // enforcement decision empty means STRICT (0%), not "no checks" — the key is
    // OMITTED (no forced 0, FE #156 pruning contract).
    ...(form.tolerance_value !== ""
      ? { tolerance_value: Number(form.tolerance_value) }
      : {}),
    description: form.description,
    key_requirements: form.key_requirements,
    // Commission three-state (V2): no_commission clears the split; otherwise
    // filled split → "X / Y", empty split → Negotiable on the card.
    no_commission: !!form.no_commission,
    commission_split:
      !form.no_commission &&
      (form.commission_posting !== "" || form.commission_responding !== "")
        ? `${form.commission_posting || 0} / ${form.commission_responding || 0}`
        : "",
    commission_split_note: form.no_commission ? "" : form.commission_note,
  });

  const submitPayload = async (payload) => {
    setSubmitting(true);
    try {
      const res = await onSubmit(payload);
      if (res && res.success === false) {
        // Soft club-name guard (V2): the backend flags free text that appears
        // to name the client club. Not a hard block — the dialog lets the
        // agent edit the text or publish anyway (same payload + confirm flag).
        if (res.status === 400 && res.data?.code === "club_name_in_text") {
          setClubNameGuard({ payload, warnings: res.data.warnings || [] });
          return;
        }
        toast.error(res.error || "We couldn't save your request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    await submitPayload(buildPayload());
  };

  // ── Live card preview (V2, rail ≥1280) — the anonymized card exactly as the
  // feed will render it: the SHARED AgentTenderCard fed with the form's
  // current values mapped onto the serializer field names (buildPayload
  // already speaks serializer). Never the club — only coarse country.
  const authUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch (err) {
      return null;
    }
  }, []);

  const previewTender = useMemo(() => {
    const p = buildPayload();
    delete p.club_id;
    delete p.wyscout_club_id;
    const clubCountry = form.club?.country;
    const countryName =
      (clubCountry && typeof clubCountry === "object"
        ? clubCountry.name
        : clubCountry) || "";
    const agentName = [authUser?.first_name, authUser?.last_name]
      .filter(Boolean)
      .join(" ");
    const selected = windowOptions.find((c) => c.value === form.target_window);
    return {
      ...p,
      id: "preview",
      verification_status: isVerified ? "club_verified" : "unverified",
      country: countryName ? { name: countryName } : null,
      league_tier: form.club?.league_tier || null,
      target_window_label: selected
        ? selected.label
        : formatTargetWindow(form.target_window),
      transfer_window: null,
      budget_band: "",
      trust_stats: null,
      is_own_agency: true,
      is_saved: false,
      posted_agency: authUser?.agency_name ? { name: authUser.agency_name } : null,
      responsible_agent: agentName ? { display_name: agentName } : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isVerified, authUser, windowOptions]);

  const salarySuffix = isStaff ? "mo" : "month";

  return (
    <form onSubmit={handleSubmit} className="flex items-start gap-5">
      <div className="flex-1 min-w-0 flex flex-col gap-3.5">
        {commission ? (
          // B2 — a commissioned hand-over. The club commissioned this search; the
          // core criteria come from their brief (locked) and the request is Club
          // Verified from birth (no OTP, Annex B6). This REPLACES the ordinary
          // verify-freeze warning below.
          <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl px-4 py-3.5 flex gap-2.5">
            <IcShieldCheck className="flex-none mt-[1px]" />
            <div className="text-[12.5px] text-[#065F46]">
              <b>Commissioned by {commission.clubName || "the club"}.</b> The core
              search criteria come from their brief and are set by the club. Your
              request goes live <b>Club Verified</b> — nothing to confirm.
            </div>
          </div>
        ) : (
          isVerified && (
            // Freeze warning per artboard — verbatim warning-triangle SVG, amber
            // #FFFBEB/#FDE68A box, copy 1:1 (rule unchanged).
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl px-4 py-3.5 flex gap-2.5">
              <IcWarnTriangle className="flex-none mt-[1px]" />
              <div className="text-[12.5px] text-[#78350F]">
                <b>This request is club-verified.</b> Changing the role or
                position, request type, level, club, target period, or commission
                resets it to <b>Unverified</b> · the badge is removed and you'll
                need a fresh club confirmation.
              </div>
            </div>
          )
        )}

        <PublicationClarity />

        <Section title="Client club" required>
          <Locked on={!!commission}>
            <ClubPicker
              value={form.club}
              onChange={(club) => set({ club })}
              error={errors.club}
              label=""
              helpText=""
            />
          </Locked>
        </Section>

        <Section title="Request details">
          {/* Two-axis model (Part B): SUBJECT (who) and LEVEL (which tier) are two
              independent choices. Subject picks player-vs-staff spec fields; Level
              (Professional/Youth) is required for BOTH and is a different concept
              from the club/league tier — it is the recruiting level, not a league. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
            <Field label="Subject">
              <Locked on={isLocked("tender_category")}>
                <Segmented
                  options={AGENT_SUBJECT_CHOICES}
                  value={form.tender_category}
                  ariaLabel="Subject"
                  onChange={(v) => {
                    const nextIsStaff = v === "staff";
                    set({
                      tender_category: nextIsStaff ? "staff" : "player",
                      // Reset the subject-specific spec fields so a switch never
                      // carries a stale position into a staff request or vice versa.
                      position: "",
                      staff_role: "",
                      type_of_tender: nextIsStaff ? "full_time" : "buy",
                    });
                  }}
                />
              </Locked>
            </Field>

            <Field
              label="Level"
              hint={isStaff ? "Staff + Youth is valid · e.g. an academy coach." : undefined}
            >
              <Locked on={isLocked("recruiting_for")}>
                <Segmented
                  options={AGENT_LEVEL_CHOICES}
                  value={normalizeAgentLevel(form.recruiting_for)}
                  ariaLabel="Level"
                  onChange={(v) => set({ recruiting_for: v })}
                />
              </Locked>
            </Field>

            {isStaff ? (
              <div>
                <Field label="Staff role" required error={errors.staff_role}>
                  <Locked on={isLocked("staff_role")}>
                    <FormSelect
                      options={staffRoleOptions}
                      value={form.staff_role}
                      onChange={(v) => set({ staff_role: v })}
                      placeholder="Select a staff role"
                      searchable
                      error={!!errors.staff_role}
                    />
                  </Locked>
                </Field>
                {/* V2: "Other" reveals a required free-text role title, shown on
                    the card as the role (e.g. Set-piece Coach, Nutritionist). */}
                {form.staff_role === "other" && (
                  <Field
                    label="Role title"
                    required
                    className="mt-3.5"
                    hint="Free text, required. Shown on the card as the role · e.g. Set-piece Coach, Nutritionist, Kit Manager."
                    error={errors.other_staff_position}
                  >
                    <InputBox
                      inputProps={{
                        type: "text",
                        value: form.other_staff_position,
                        onChange: (e) => set({ other_staff_position: e.target.value }),
                        placeholder: "e.g. Set-piece Coach",
                      }}
                    />
                  </Field>
                )}
              </div>
            ) : (
              <Field label="Position" required error={errors.position}>
                <Locked on={isLocked("position")}>
                  <FormSelect
                    options={positionChoices}
                    value={form.position}
                    onChange={(v) => set({ position: v })}
                    placeholder="Select a position"
                    searchable
                    error={!!errors.position}
                  />
                </Locked>
              </Field>
            )}

            {isStaff && (
              <Field label="Request type">
                <Locked on={isLocked("type_of_tender")}>
                <div className="flex flex-wrap gap-2">
                  {typeChoices.map((t) => (
                    <Pill
                      key={t.value}
                      large
                      active={form.type_of_tender === t.value}
                      onClick={() => set({ type_of_tender: t.value })}
                    >
                      {TYPE_LABEL_OVERRIDES[t.value] || t.label}
                    </Pill>
                  ))}
                </div>
                {/* V2: Temporary staff type carries an optional duration (number
                    + weeks/months/years) — the "12-month contract" chip. */}
                {form.type_of_tender === "temporary" && (
                  <div className="flex items-center gap-2 mt-[9px]">
                    <InputBox
                      className="w-20 flex-none"
                      inputProps={{
                        type: "number",
                        min: "1",
                        value: form.temporary_duration,
                        onChange: (e) => set({ temporary_duration: e.target.value }),
                        placeholder: "12",
                        "aria-label": "Duration",
                      }}
                    />
                    <FormSelect
                      options={DURATION_UNIT_CHOICES}
                      value={form.temporary_duration_unit}
                      onChange={(v) => set({ temporary_duration_unit: v })}
                      className="w-[130px] flex-none"
                    />
                    <span className="text-[11.5px] text-[#94A3B8]">optional</span>
                  </div>
                )}
                </Locked>
              </Field>
            )}

            <Field label="Currency" hint="EUR · GBP · CHF · USD · default EUR">
              <Locked on={isLocked("currency")}>
                <FormSelect
                  options={currencyOptions}
                  value={form.currency}
                  onChange={(v) => set({ currency: v })}
                />
              </Locked>
            </Field>
          </div>

          {!isStaff && (
            <>
              <Field label="Request type">
                <div className="flex flex-wrap gap-2">
                  {typeChoices.map((t) => (
                    <Pill
                      key={t.value}
                      large
                      active={form.type_of_tender === t.value}
                      onClick={() => set({ type_of_tender: t.value })}
                    >
                      {TYPE_LABEL_OVERRIDES[t.value] || t.label}
                    </Pill>
                  ))}
                </div>
              </Field>

              {isLoan && (
                <div className="flex flex-col gap-3">
                  <Checkbox
                    checked={form.loan_with_option_to_buy}
                    onChange={(e) => set({ loan_with_option_to_buy: e.target.checked })}
                  >
                    <span>
                      Loan with option to buy{" "}
                      <span className="text-[#94A3B8]">· default on</span>
                    </span>
                  </Checkbox>
                  <Checkbox
                    checked={form.has_loan_fee}
                    onChange={(e) => set({ has_loan_fee: e.target.checked })}
                    disabled={form.only_free_players}
                  >
                    Loan fee
                  </Checkbox>
                  {form.only_free_players && (
                    <p className="text-[11.5px] text-[#B45309] font-semibold ml-[26px]">
                      Only free players · no transfer or loan fee applies. Both
                      fields are cleared and locked.
                    </p>
                  )}
                  {form.has_loan_fee && !form.only_free_players && (
                    <div className="flex items-center gap-2 ml-[26px]">
                      <InputBox
                        className="w-[180px] flex-none"
                        inputProps={{
                          type: "number",
                          value: form.loan_fee_amount,
                          onChange: (e) => set({ loan_fee_amount: e.target.value }),
                          placeholder: "Loan fee amount",
                          "aria-label": "Loan fee amount",
                        }}
                      />
                      <span className="text-[12.5px] text-[#94A3B8]">
                        {CURRENCY_DISPLAY[form.currency] || form.currency}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {isTrial && (
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <Field label="Trial start" required>
                      <InputBox
                        inputProps={{
                          type: "date",
                          value: form.trial_start,
                          onChange: (e) => set({ trial_start: e.target.value }),
                          "aria-label": "Trial start",
                        }}
                      />
                    </Field>
                    <Field label="Trial end" required>
                      <InputBox
                        inputProps={{
                          type: "date",
                          value: form.trial_end,
                          onChange: (e) => set({ trial_end: e.target.value }),
                          "aria-label": "Trial end",
                        }}
                      />
                    </Field>
                  </div>
                  <p className="mt-[5px] text-[11.5px] text-[#94A3B8]">
                    End date must be on or after the start date.
                  </p>
                  {errors.trial && (
                    <p className="mt-1 text-sm text-red-500">{errors.trial}</p>
                  )}
                </div>
              )}

              {/* Player eligibility — "Only free players" gates the fee/loan-fee:
                  a free agent carries no transfer or loan fee, so checking it
                  clears and disables those inputs (the form never lets the
                  free-players↔fee inconsistency reach the backend validation). */}
              <div className="flex flex-wrap items-center gap-[18px]">
                <span className="inline-flex items-center gap-[7px]">
                  <Checkbox
                    checked={form.only_free_players}
                    onChange={(e) =>
                      set(
                        e.target.checked
                          ? {
                              only_free_players: true,
                              maximum_transfer_fee: "",
                              has_loan_fee: false,
                              loan_fee_amount: "",
                            }
                          : { only_free_players: false },
                      )
                    }
                  >
                    Only free players
                  </Checkbox>
                  <Tooltip
                    content="Only free players · no transfer or loan fee applies."
                    position="top"
                    className="whitespace-normal max-w-[240px]"
                  >
                    <span className="inline-flex cursor-default"><IcInfo /></span>
                  </Tooltip>
                </span>
                <Checkbox
                  checked={form.only_eu_player}
                  onChange={(e) => set({ only_eu_player: e.target.checked })}
                >
                  EU players only
                </Checkbox>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
                {isBuy && (
                  <Field
                    label="Maximum transfer fee"
                    hint={
                      form.only_free_players
                        ? undefined
                        : "Leave blank for a free transfer."
                    }
                    error={errors.maximum_transfer_fee}
                  >
                    {form.only_free_players ? (
                      // Locked state as drawn: grey box, verbatim lock + middot.
                      <>
                        <div className="h-10 flex items-center gap-2 px-3 rounded-[9px] border border-[#E2E8F0] bg-[#F1F5F9] text-[13.5px] text-[#94A3B8]">
                          <IcLock className="flex-none" />
                          ·
                        </div>
                        <p className="mt-[5px] text-[11.5px] text-[#B45309] font-semibold">
                          Only free players · no transfer or loan fee applies.
                          Both fields are cleared and locked.
                        </p>
                      </>
                    ) : (
                      <InputBox
                        suffix={CURRENCY_SYMBOL[form.currency] || form.currency}
                        inputProps={{
                          type: "number",
                          value: form.maximum_transfer_fee,
                          onChange: (e) => set({ maximum_transfer_fee: e.target.value }),
                          "aria-label": "Maximum transfer fee",
                        }}
                      />
                    )}
                  </Field>
                )}

                {/* V2 period selector (player, optional): which window the request
                    targets. Choices come from the club country's TransferWindow
                    table (window-choices endpoint) with generic half-year
                    fallbacks; "Not specified" = null. */}
                <Field label="Which window is this for?" sub="(optional)">
                  <div className="flex flex-wrap gap-[7px]" role="radiogroup" aria-label="Target window">
                    <Pill
                      active={form.target_window === ""}
                      onClick={() => set({ target_window: "" })}
                    >
                      Not specified
                    </Pill>
                    {windowOptions.map((c) => (
                      <Pill
                        key={c.value}
                        active={form.target_window === c.value}
                        onClick={() => set({ target_window: c.value })}
                        title={
                          c.opens_at && c.closes_at
                            ? `${c.opens_at} - ${c.closes_at}`
                            : undefined
                        }
                      >
                        {c.label}
                      </Pill>
                    ))}
                  </div>
                </Field>
              </div>
            </>
          )}
        </Section>

        <Section title="Requirements">
          {isStaff && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
              {/* V2 experience: free minimum years or "Not important" — no fixed
                  buckets (replaces the old EXPERIENCE_CHOICES dropdown). */}
              <Field
                label="Experience"
                hint="Free minimum · from 1, from 5, from 10 · no fixed buckets."
                error={errors.experience_min_years}
              >
                <div className="flex items-center gap-2.5 flex-wrap">
                  <InputBox
                    className="w-[150px] flex-none"
                    prefix="from"
                    suffix="years"
                    disabled={form.experience_not_important}
                    inputProps={{
                      type: "number",
                      min: "0",
                      max: "60",
                      value: form.experience_min_years,
                      onChange: (e) => set({ experience_min_years: e.target.value }),
                      placeholder: "5",
                      "aria-label": "Experience minimum years",
                    }}
                  />
                  <span className="text-xs text-[#94A3B8]">or</span>
                  <Checkbox
                    checked={form.experience_not_important}
                    onChange={(e) =>
                      set(
                        e.target.checked
                          ? { experience_not_important: true, experience_min_years: "" }
                          : { experience_not_important: false },
                      )
                    }
                  >
                    Not important
                  </Checkbox>
                </div>
              </Field>

              {/* V2 staff start date: Immediately / From month / Not specified. */}
              <Field label="Start date" error={errors.staff_start_month}>
                <div className="flex flex-wrap gap-[7px]" role="radiogroup" aria-label="Start date">
                  {STAFF_START_TYPE_CHOICES.map((opt) => (
                    <Pill
                      key={opt.value}
                      active={form.staff_start_type === opt.value}
                      onClick={() =>
                        set(
                          opt.value === "from_month"
                            ? { staff_start_type: "from_month" }
                            : { staff_start_type: opt.value, staff_start_month: "" },
                        )
                      }
                    >
                      {START_LABELS[opt.value] || opt.label}
                    </Pill>
                  ))}
                </div>
                {form.staff_start_type === "from_month" && (
                  <div className="mt-[9px]">
                    <InputBox
                      className="w-[200px]"
                      inputProps={{
                        type: "month",
                        value: form.staff_start_month,
                        onChange: (e) => set({ staff_start_month: e.target.value }),
                        "aria-label": "Start month",
                      }}
                    />
                  </div>
                )}
              </Field>
            </div>
          )}

          <Field label="Salary" required error={errors.salary}>
            <div className="flex flex-wrap gap-2 mb-[9px]">
              <MiniSegmented
                options={SALARY_CALC_OPTIONS}
                value={form.salary_calculation}
                ariaLabel="Salary calculation"
                onChange={(v) => set({ salary_calculation: v })}
              />
              <MiniSegmented
                options={SALARY_KIND_OPTIONS}
                value={form.salary_type}
                ariaLabel="Salary type"
                onChange={(v) => set({ salary_type: v })}
              />
            </div>
            {form.salary_type === "salary_between" ? (
              <div className="grid grid-cols-2 gap-2.5">
                <InputBox
                  suffix={`from / ${salarySuffix}`}
                  inputProps={{
                    type: "number",
                    value: form.salary_from,
                    onChange: (e) => set({ salary_from: e.target.value }),
                    placeholder: "From",
                  }}
                />
                <InputBox
                  suffix={`to / ${salarySuffix}`}
                  inputProps={{
                    type: "number",
                    value: form.salary_to,
                    onChange: (e) => set({ salary_to: e.target.value }),
                    placeholder: "To",
                  }}
                />
              </div>
            ) : (
              <InputBox
                suffix={`/ ${salarySuffix}`}
                inputProps={{
                  type: "number",
                  value: form.salary_fixed,
                  onChange: (e) => set({ salary_fixed: e.target.value }),
                  placeholder: "Amount",
                }}
              />
            )}
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
            {/* Market value — player only (V2 staff cleanup). The old budget-band
                tooltip is dead: real figures are published as entered. */}
            {!isStaff && (
              <Field
                label="Market value"
                tooltip="The player's estimated market value range."
                hint={
                  form.market_value_type !== "not_important"
                    ? "Published as entered · other agents see these figures."
                    : undefined
                }
                hintTone="amber"
              >
                <div className="flex flex-wrap gap-2.5 mb-[9px]">
                  {MARKET_VALUE_CHOICES.map((opt) => (
                    <Radio
                      key={opt.value}
                      name="market_value_type"
                      checked={form.market_value_type === opt.value}
                      onChange={() => set({ market_value_type: opt.value })}
                    >
                      {MV_LABELS[opt.value] || opt.label}
                    </Radio>
                  ))}
                </div>
                {form.market_value_type === "market_value_range" && (
                  <div className="grid grid-cols-2 gap-2.5">
                    <InputBox
                      inputProps={{
                        type: "number",
                        value: form.market_value_from,
                        onChange: (e) => set({ market_value_from: e.target.value }),
                        placeholder: "From",
                      }}
                    />
                    <InputBox
                      inputProps={{
                        type: "number",
                        value: form.market_value_to,
                        onChange: (e) => set({ market_value_to: e.target.value }),
                        placeholder: "To",
                      }}
                    />
                  </div>
                )}
                {form.market_value_type === "market_value_fixed" && (
                  <InputBox
                    inputProps={{
                      type: "number",
                      value: form.market_value_fixed,
                      onChange: (e) => set({ market_value_fixed: e.target.value }),
                      placeholder: "Amount",
                    }}
                  />
                )}
              </Field>
            )}

            <Field label="Age range">
              <div className="flex flex-wrap gap-2.5 mb-[9px]">
                {IMPORTANCE_CHOICES.map((opt) => (
                  <Radio
                    key={opt.value}
                    name="age_range_in"
                    checked={form.age_range_in === opt.value}
                    onChange={() =>
                      set(
                        opt.value === "not_important"
                          ? { age_range_in: "not_important", min_age: "", max_age: "" }
                          : { age_range_in: "specify" },
                      )
                    }
                  >
                    {opt.label}
                  </Radio>
                ))}
              </div>
              {form.age_range_in === "specify" && (
                <div className="grid grid-cols-2 gap-2.5">
                  <InputBox
                    suffix="min"
                    inputProps={{
                      type: "number",
                      value: form.min_age,
                      onChange: (e) => set({ min_age: e.target.value }),
                      "aria-label": "Minimum age",
                    }}
                  />
                  <InputBox
                    suffix="max"
                    inputProps={{
                      type: "number",
                      value: form.max_age,
                      onChange: (e) => set({ max_age: e.target.value }),
                      "aria-label": "Maximum age",
                    }}
                  />
                </div>
              )}
            </Field>

            {!isStaff && (
              <>
                <Field label="Minimum height">
                  <div className="flex flex-wrap gap-2.5 mb-[9px]">
                    {IMPORTANCE_CHOICES.map((opt) => (
                      <Radio
                        key={opt.value}
                        name="height_in"
                        checked={form.height_in === opt.value}
                        onChange={() =>
                          set(
                            opt.value === "not_important"
                              ? { height_in: "not_important", minimum_height: "" }
                              : { height_in: "specify" },
                          )
                        }
                      >
                        {opt.label}
                      </Radio>
                    ))}
                  </div>
                  {form.height_in === "specify" && (
                    <InputBox
                      className="w-[120px]"
                      suffix="cm"
                      inputProps={{
                        type: "number",
                        value: form.minimum_height,
                        onChange: (e) => set({ minimum_height: e.target.value }),
                        "aria-label": "Minimum height",
                      }}
                    />
                  )}
                </Field>

                <Field label="Preferred foot">
                  <div className="flex flex-wrap gap-[7px]" role="radiogroup" aria-label="Preferred foot">
                    {footOptions.map((opt) => (
                      <Pill
                        key={opt.value}
                        active={form.foot === opt.value}
                        onClick={() => set({ foot: opt.value })}
                      >
                        {opt.label}
                      </Pill>
                    ))}
                  </div>
                </Field>

                {/* "Games played in" radio mirrors the club form
                    (GAMES_PLAYED_CHOICES): a period with a count, or "Not
                    important". Player only (V2 staff cleanup). */}
                <Field label="Games played in">
                  <div className="flex flex-wrap gap-2.5 mb-[9px]">
                    {GAMES_PLAYED_CHOICES.map((opt) => (
                      <Radio
                        key={opt.value}
                        name="games_played_in"
                        checked={form.games_played_in === opt.value}
                        onChange={() => set({ games_played_in: opt.value })}
                      >
                        {GAMES_LABELS[opt.value] || opt.label}
                      </Radio>
                    ))}
                  </div>
                  {form.games_played_in === "last_season" && (
                    <InputBox
                      className="w-[120px]"
                      suffix="games"
                      inputProps={{
                        type: "number",
                        value: form.matches_played_last_season,
                        onChange: (e) =>
                          set({ matches_played_last_season: e.target.value }),
                        "aria-label": "Games last season",
                      }}
                    />
                  )}
                  {form.games_played_in === "last_half_season" && (
                    <InputBox
                      className="w-[120px]"
                      suffix="games"
                      inputProps={{
                        type: "number",
                        value: form.matches_played_last_half_season,
                        onChange: (e) =>
                          set({ matches_played_last_half_season: e.target.value }),
                        "aria-label": "Games last half-season",
                      }}
                    />
                  )}
                </Field>
              </>
            )}

            {/* Tolerance: empty = strict 0% (enforcement decision 2026-08-12), no forced
                0. Creator-side only — never published. */}
            <Field
              label="Tolerance"
              sub="(optional)"
              tooltip="How much flexibility on the criteria (0 to 50%)."
              hint="Empty = strict (0%) — requirements always apply; tolerance only widens them. Internal: other agents never see this."
              error={errors.tolerance_value}
            >
              <InputBox
                className="w-[120px]"
                suffix="%"
                inputProps={{
                  type: "number",
                  min: "0",
                  max: "50",
                  value: form.tolerance_value,
                  onChange: (e) => set({ tolerance_value: e.target.value }),
                  placeholder: "e.g. 10",
                  "aria-label": "Tolerance",
                }}
              />
            </Field>
          </div>

          {/* Free text is PUBLIC in V2 (approved) — warn inline, and the backend
              club-name guard backs the warning with an actual check. */}
          <Field
            label="Description"
            sub="· public on the request preview · never name the club here"
          >
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Describe the request"
              className={`${textareaCls} min-h-[76px]`}
            />
          </Field>

          <Field
            label="Key requirements"
            sub="· public on the request preview · never name the club here"
          >
            <textarea
              rows={2}
              value={form.key_requirements}
              onChange={(e) => set({ key_requirements: e.target.value })}
              placeholder="e.g. availability, contract expectations…"
              className={`${textareaCls} min-h-[56px]`}
            />
          </Field>
        </Section>

        <Section title="Commission">
          <CommissionSplitField
            posting={form.commission_posting}
            responding={form.commission_responding}
            note={form.commission_note}
            noCommission={form.no_commission}
            onChange={(patch) =>
              set({
                ...(patch.posting !== undefined && { commission_posting: patch.posting }),
                ...(patch.responding !== undefined && {
                  commission_responding: patch.responding,
                }),
                ...(patch.note !== undefined && { commission_note: patch.note }),
                ...(patch.noCommission !== undefined && {
                  no_commission: patch.noCommission,
                }),
              })
            }
          />
        </Section>

        {/* Footer CTAs: right-aligned 44px pair on desktop; stacked full-width
            on mobile with the 48px primary FIRST (artboard 390). */}
        <div className="flex flex-col-reverse gap-2.5 pt-1 pb-6 sm:flex-row sm:items-center sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="h-11 rounded-[11px] sm:rounded-[10px] border border-[#E2E8F0] bg-white px-[18px] text-[13.5px] font-semibold text-[#334155] hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="h-12 sm:h-11 rounded-[11px] sm:rounded-[10px] bg-[#2283FF] px-[22px] text-[14px] sm:text-[13.5px] font-semibold text-white hover:bg-[#1B6FE0] disabled:opacity-50"
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>

      {/* Live card preview rail (≥1280): the clarity box, made visual — the
          SHARED feed card with the form's current values. Never forked.
          Rail widths per artboard: 300px @1280, 320px @1440, 340px @1920+. */}
      <div className="hidden xl:block w-[300px] min-[1440px]:w-[320px] min-[1920px]:w-[340px] flex-none">
        <div className="sticky top-4 flex flex-col gap-2.5">
          <div className="text-[11px] font-extrabold uppercase tracking-[.06em] text-[#94A3B8]">
            Live preview · what other agents see
          </div>
          <AgentTenderCard tender={previewTender} />
          <p className="text-[11.5px] text-[#64748B] bg-white border border-dashed border-[#E2E8F0] rounded-[10px] px-3 py-2.5">
            The badge turns <b className="text-[#047857]">Club Verified</b>{" "}
            once your club confirms · offered right after posting.
          </p>
        </div>
      </div>

      {/* Club-name guard dialog (soft, never a block): backend flagged the
          public free text as naming the client club. */}
      {clubNameGuard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Your text may name the club"
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <IcWarnTriangle size={20} className="flex-shrink-0" />
              <h3 className="text-base font-semibold">
                Your text may name the club
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Description and key requirements are public on the request
              preview · never name the club in them.
            </p>
            {clubNameGuard.warnings.length > 0 && (
              <ul className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 space-y-1">
                {clubNameGuard.warnings.map((w, i) => (
                  <li key={`${w.field}-${i}`}>
                    <span className="font-medium">
                      {w.field === "key_requirements"
                        ? "Key requirements"
                        : "Description"}
                      :
                    </span>{" "}
                    "{w.match}"
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setClubNameGuard(null)}
                className="px-5 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Edit text
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={async () => {
                  const { payload } = clubNameGuard;
                  setClubNameGuard(null);
                  await submitPayload({ ...payload, confirm_club_name: true });
                }}
                className="px-5 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
              >
                Publish anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default AgentTenderForm;
